import globalAxios from "axios";
import axiosRetry, { exponentialDelay, isNetworkOrIdempotentRequestError } from "axios-retry";

import config from "@/app/config.js";
import router from "@/app/router";
import i18n from "@/i18n";
import store from "../store";
import { toaster } from "./toaster";

const isOnPrem = config.VITE_APP_IS_ONPREM === "on";
const isOnPremUsbMode = config.VITE_APP_PREM_USB_MODE === "true";
const onPremAuthToken = config.VITE_APP_PREM_AUTH_TOKEN;

let baseURL = config.VITE_APP_ROOT_API;

if (isOnPrem) {
  baseURL = baseURL.replace("api/", "");
}

const axios = globalAxios.create({
  baseURL,
  // FIXME: we disable withCredentials for on-premise due to unresolved
  // CORS issues with platform backend
  withCredentials: !isOnPrem,
});

const NETWORK_RETRY_COUNT = 3;

axiosRetry(axios, {
  retries: NETWORK_RETRY_COUNT,
  retryDelay: exponentialDelay,
  retryCondition: (error) => {
    if (error.config?._retry) {
      return false;
    }
    return isNetworkOrIdempotentRequestError(error);
  },
});

let pendingRequests = [];

const processPendingRequests = (error = null) => {
  const requests = [...pendingRequests];
  pendingRequests = [];

  if (error) {
    requests.forEach((request) => request.reject(error));
  } else {
    requests.forEach((request) => {
      const token = localStorage.getItem("token");
      request.config.headers["Authorization"] = `Bearer ${token}`;

      if (request.config.url?.includes("verify")) {
        request.config.data = { token };
      }

      request.resolve(axios(request.config));
    });
  }
};

const enhanceErrorWithContext = (error, originalRequest) => {
  const enhancedError = new Error(error.message || "API Request Failed");

  enhancedError.context = {
    request: {
      url: originalRequest?.url,
      method: originalRequest?.method,
      headers: originalRequest?.headers ? Object.keys(originalRequest.headers) : [],
      params: originalRequest?.params,
      baseURL: originalRequest?.baseURL,
    },
    response: {
      status: error?.response?.status,
      statusText: error?.response?.statusText,
      data: error?.response?.data,
      headers: error?.response?.headers,
    },
    timestamp: new Date().toISOString(),
    userId: store.getters.getUser?.id || null,
    userEmail: store.getters.getUser?.email || null,
  };

  enhancedError.stack = error.stack;
  enhancedError.originalError = error;

  return enhancedError;
};

const isExpectedError = (error, originalRequest) => {
  const status = error?.response?.status;

  const isTokenError =
    status === 401 &&
    (error?.response?.data?.exp === "token expired" || error?.response?.data?.code === "token_not_valid");

  const isRetryAttempt = originalRequest?._retry === true;

  const isRefreshEndpointError = originalRequest?.url?.includes("refresh") && status === 401;

  return isTokenError || isRetryAttempt || isRefreshEndpointError;
};

axios.interceptors.response.use(
  (response) => {
    // Check x-prem-auth-token header in on-premise USB mode
    const token = localStorage.getItem("token");

    if (token?.length > 0 && isOnPrem && isOnPremUsbMode) {
      const premHeaderValue = response.headers["x-prem-auth-token"];

      if (!premHeaderValue || premHeaderValue !== onPremAuthToken) {
        store.dispatch("logout");
        window.location.reload();
        const error = new Error("Invalid token");
        error.code = "INVALID_TOKEN";

        throw error;
      }
    }

    return response;
  },
  async (error) => {
    const status = error?.response?.status || 500;
    const loggedUser = store.getters.getUser;
    const originalRequest = error.config;

    if (!error.response) {
      const t = i18n?.global?.t || i18n?.t;
      if (t) {
        toaster.error(t("feedback.error.network.title"), {
          id: "network-error",
          duration: 8000,
          description: t("feedback.error.network.description"),
        });
      }

      if (!loggedUser) {
        window.location.href = "/login";
        localStorage.clear();
      }

      return Promise.reject(error);
    }

    const nonRedirectRoutes = ["login", "register", "password-recovery", "confirm-new-password"];
    const isNonRedirectRoute = nonRedirectRoutes.some((i) => router.currentRoute.value.fullPath.includes(i));
    const isTokenExpired = status === 401 && error?.response?.data?.exp === "token expired";
    const isTokenNotValid = status === 401 && error?.response?.data?.code === "token_not_valid";
    const isRefreshEndpoint = originalRequest.url.includes("refresh");
    const isTokenRefreshNeeded =
      status === 401 &&
      !isNonRedirectRoute &&
      (isTokenExpired || isTokenNotValid) &&
      !isRefreshEndpoint &&
      !originalRequest._retry;

    if (isTokenRefreshNeeded) {
      originalRequest._retry = true;

      if (store.getters.isTokenRefreshing) {
        return new Promise((resolve, reject) => {
          pendingRequests.push({
            resolve,
            reject,
            config: originalRequest,
          });
        });
      }

      try {
        await store.dispatch("refreshToken");

        processPendingRequests();

        originalRequest.headers["Authorization"] = `Bearer ${localStorage.getItem("token")}`;

        return axios(originalRequest);
      } catch (refreshError) {
        processPendingRequests(refreshError);
        store.dispatch("logout");

        return Promise.reject(refreshError);
      }
    } else if (status === 401 && !isNonRedirectRoute) {
      store.dispatch("logout");

      return Promise.reject(error);
    }

    if (isExpectedError(error, originalRequest)) {
      return Promise.reject(error);
    }

    if (status >= 500) {
      const t = i18n?.global?.t || i18n?.t;
      if (t) {
        toaster.error(t("feedback.error.network.title"), {
          id: "network-error",
          duration: 8000,
          description: t("feedback.error.network.description"),
        });
      }
    }

    if (status === 404 && !originalRequest?.allow404) {
      router.push("/404");
    }

    const enhancedError = enhanceErrorWithContext(error, originalRequest);

    throw enhancedError;
  },
);

export default axios;
